import type { PayloadAction, Slice } from "@reduxjs/toolkit";
import { createAction, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import type { UpdateWorkspaceAccessResponse, WorkspaceResponse } from "@somewear/api";
import { WorkspaceAccess, WorkspaceRadioChannelUpdatedDto } from "@somewear/api";

import { emitWorkspaceRadioChannelReceived, emitWorkspaceRadioChannelUpdated } from "./radio";
import {
	emitOrganizationWorkspacesReceived,
	emitWorkspaceChangeFromServer,
	emitWorkspacesReceived,
} from "./workspace.actions";
import { PERSONAL_WORKSPACE_ID, PERSONAL_WORKSPACE_NAME } from "./workspace.model";
import type { WorkspaceState } from "./workspace.state";

export interface IWorkspace extends Omit<WorkspaceResponse.AsObject, "meshKey" | "externalId"> {
	userIsMember?: boolean;
	isNew?: boolean;
}

const adapter = createEntityAdapter<IWorkspace, string>({
	selectId: (workspace) => workspace.id,
	sortComparer: (a, b) => {
		return a.name.localeCompare(b.name);
	},
});

export const updateWorkspaceAccess =
	createAction<UpdateWorkspaceAccessResponse.AsObject>("workspace/access/update");

export const clearWorkspace = createAction<string>("workspace/clear");
export const clearActiveWorkspace = createAction("workspace/clear/active");

// Rename the exports for readability in component usage

export const {
	selectAll: selectAllWorkspaces,
	selectById: selectWorkspaceById,
	selectEntities: selectWorkspaceEntities,
} = adapter.getSelectors((state: WorkspaceState) => state.workspaces);

export const workspacesSlice: Slice = createSlice({
	name: "workspaces",
	initialState: adapter.getInitialState(),
	reducers: {
		emitWorkspaceUpdate(state, action: PayloadAction<WorkspaceResponse.AsObject>) {
			adapter.upsertOne(state, action.payload);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(emitOrganizationWorkspacesReceived, (state, action) => {
			adapter.upsertMany(state, action.payload);
		});
		builder.addCase(clearWorkspace, (state, action) => {
			adapter.removeOne(state, action.payload);
		});
		builder.addCase(emitWorkspacesReceived, (state, action) => {
			const workspaces: IWorkspace[] = action.payload.map((workspace: IWorkspace) => {
				const editedWorkspace = { ...workspace };
				if (workspace.name.isEmpty()) editedWorkspace.name = PERSONAL_WORKSPACE_NAME;
				if (workspace.id.isEmpty()) editedWorkspace.id = PERSONAL_WORKSPACE_ID;
				editedWorkspace.userIsMember = true;
				return editedWorkspace;
			});
			adapter.upsertMany(state, workspaces);
		});
		builder.addCase(emitWorkspaceChangeFromServer, (state, action) => {
			const access = action.payload.access;
			const id = action.payload.id;

			if (
				access === WorkspaceAccess.WORKSPACEACCESSCLOSED ||
				access == WorkspaceAccess.WORKSPACEACCESSDELETED
			) {
				adapter.removeOne(state, id);
			} else {
				adapter.upsertOne(state, action.payload);
			}
		});
		builder.addCase(emitWorkspaceRadioChannelReceived, (state, action) => {
			if (!action.payload.radioChannel) return;
			const workspace = adapter.getSelectors().selectById(state, action.payload.workspaceId);
			if (workspace === undefined) return;

			adapter.upsertOne(state, {
				...workspace,
				radioChannelsList: [...workspace.radioChannelsList, action.payload.radioChannel],
			});
		});
		builder.addCase(emitWorkspaceRadioChannelUpdated, (state, action) => {
			const workspace = adapter.getSelectors().selectById(state, action.payload.workspaceId);

			if (workspace === undefined) return;

			const updatedRadioChannels = workspace.radioChannelsList
				.map((radioChannel) => {
					if (radioChannel.id === action.payload.radioChannelId) {
						if (
							action.payload.status === WorkspaceRadioChannelUpdatedDto.Status.REMOVED
						) {
							return undefined;
						} else if (
							action.payload.status ===
							WorkspaceRadioChannelUpdatedDto.Status.ARCHIVED
						) {
							return { ...radioChannel, isArchived: true };
						}
					}
					return radioChannel;
				})
				.filter((radioChannel) => radioChannel !== undefined);
			adapter.upsertOne(state, { ...workspace, radioChannelsList: updatedRadioChannels });
		});
	},
});
